import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { usePWA } from '../hooks/usePWA';
import { useMobile } from '../hooks/useMobile';

const { FiDownload, FiX, FiSmartphone, FiMonitor } = FiIcons;

const InstallPrompt = () => {
  const { canInstall, installApp, isStandalone } = usePWA();
  const { isMobile } = useMobile();
  const [showPrompt, setShowPrompt] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check if user has already dismissed the prompt
    const hasBeenDismissed = localStorage.getItem('pwa-install-dismissed');
    
    if (canInstall && !hasBeenDismissed && !isStandalone) {
      // Show prompt after a short delay
      const timer = setTimeout(() => {
        setShowPrompt(true);
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [canInstall, isStandalone]);

  const handleInstall = async () => {
    const success = await installApp();
    if (success) {
      setShowPrompt(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setDismissed(true);
    localStorage.setItem('pwa-install-dismissed', 'true');
  };

  const handleRemindLater = () => {
    setShowPrompt(false);
    // Set a timestamp to show again in 7 days
    const remindDate = Date.now() + (7 * 24 * 60 * 60 * 1000);
    localStorage.setItem('pwa-install-remind', remindDate.toString());
  };

  if (!showPrompt || isStandalone || dismissed) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          className="bg-gradient-to-br from-gray-900 to-gray-800 border border-gray-700 rounded-2xl p-6 w-full max-w-md shadow-2xl"
          initial={{ scale: 0.8, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.8, opacity: 0, y: 20 }}
          transition={{ type: 'spring', damping: 20 }}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <img 
                src="https://res.cloudinary.com/frontly/image/upload/v1756676497/Angry_Branding_Logo_ykea75.png"
                alt="Angry Coder"
                className="w-10 h-10 rounded-lg mr-3"
              />
              <h3 className="text-xl font-bold text-white">Install Angry Coder</h3>
            </div>
            <button
              onClick={handleDismiss}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <SafeIcon icon={FiX} className="text-xl" />
            </button>
          </div>

          {/* Content */}
          <div className="mb-6">
            <div className="flex items-center justify-center mb-4">
              <SafeIcon 
                icon={isMobile ? FiSmartphone : FiMonitor} 
                className="text-6xl text-orange-400" 
              />
            </div>
            
            <h4 className="text-lg font-semibold text-white mb-2 text-center">
              Get the Native App Experience
            </h4>
            
            <p className="text-gray-300 text-center mb-4">
              Install Angry Coder for faster access, offline support, and a native app experience.
            </p>

            {/* Features */}
            <div className="space-y-3 mb-6">
              <div className="flex items-center text-sm text-gray-300">
                <div className="w-2 h-2 bg-green-400 rounded-full mr-3"></div>
                <span>⚡ Instant loading and faster performance</span>
              </div>
              <div className="flex items-center text-sm text-gray-300">
                <div className="w-2 h-2 bg-blue-400 rounded-full mr-3"></div>
                <span>📱 Native app experience with offline support</span>
              </div>
              <div className="flex items-center text-sm text-gray-300">
                <div className="w-2 h-2 bg-purple-400 rounded-full mr-3"></div>
                <span>🔔 Push notifications for updates</span>
              </div>
              <div className="flex items-center text-sm text-gray-300">
                <div className="w-2 h-2 bg-orange-400 rounded-full mr-3"></div>
                <span>🏠 Add to home screen for quick access</span>
              </div>
            </div>

            {/* Mobile specific instructions */}
            {isMobile && (
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 mb-4">
                <p className="text-blue-200 text-sm text-center">
                  📱 <strong>Mobile Tip:</strong> After installing, you can access Angry Coder 
                  directly from your home screen like any native app!
                </p>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="space-y-3">
            <button
              onClick={handleInstall}
              className="w-full bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white font-semibold py-3 px-4 rounded-xl transition-all duration-200 flex items-center justify-center"
            >
              <SafeIcon icon={FiDownload} className="mr-2" />
              Install Now
            </button>
            
            <div className="flex gap-2">
              <button
                onClick={handleRemindLater}
                className="flex-1 bg-gray-700 hover:bg-gray-600 text-gray-300 font-medium py-2 px-4 rounded-lg transition-colors duration-200"
              >
                Remind Later
              </button>
              <button
                onClick={handleDismiss}
                className="flex-1 bg-gray-700 hover:bg-gray-600 text-gray-300 font-medium py-2 px-4 rounded-lg transition-colors duration-200"
              >
                No Thanks
              </button>
            </div>
          </div>

          {/* Privacy note */}
          <p className="text-xs text-gray-500 text-center mt-4">
            Installing creates a shortcut to our website. No additional permissions required.
          </p>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default InstallPrompt;